import WifiBoxIcon from '@/components/icons/wifi-box';
import House from '@/components/images/house';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { apiQueryKeys } from '@/services/apis/apiQueryKeys';
import { getBroadbandPlanCard } from '@/services/apis/getPlanCard';
import { CURRENCY, NBN, NONE, SCROLLABLE_IDS } from '@/constants/app.constants';
import { useMutation, useQuery } from '@tanstack/react-query';
// import type { CustomerSegmentKeyEnum } from '../schemas';
import { FullScreenLoadingSpinner } from '@/components/full-screen-loading-spinner';
import { ReplaceHtmlWithReact } from '@/lib/replace-html-with-react';
import { useGlobalStore } from '@/stores/store';
import { serviceQualificationQueryOptions } from '@/services/apis/queryOptions/serviceQualificationQueryOptions';
import { apiMutationKeys } from '@/services/apis/apiMutationKeys';
import { getQuoteSummary } from '@/services/apis/getQuoteSummary';
import { getNewDevelopmentCharge } from '@/services/apis/getNewDevelopmentCharge';
import {
  createBroadbandOfferPayload,
  createMobileOfferPayload,
} from '@/utils/quote-summary';
import { useShallow } from 'zustand/react/shallow';
import React from 'react';
import {
  gtmCTaClick,
  gtmNBNPlanCardSelect,
  gtmSelectItem,
} from '@/utils/events';
import { getGlobalStore } from '@/utils/store';
import CheckCircle from '@/components/icons/check-circle';
import { usePage } from '@/hooks/use-page';

// interface CMSPLanCardBroadBandProps {
//   customerSegment: CustomerSegmentKeyEnum;
// }

const CMSPlanCardBroadband = () => {
  const pageKey = usePage();
  const {
    setLatestBroadbandOffer,
    setLatestNewDevelopmentCharge,
    setIsShoppingCartOpen,
  } = getGlobalStore();

  const {
    latestSelectedAddresses,
    feedbackCardRecentSearchVisible,
    latestMobileOffer,
    latestBroadbandOffer,
  } = useGlobalStore(
    useShallow((store) => ({
      latestSelectedAddresses: store.addressStore.latestSelectedAddresses,
      latestMobileOffer: store.shoppingCartStore.latestMobileOffer,
      latestBroadbandOffer: store.shoppingCartStore.latestBroadbandOffer,
      feedbackCardRecentSearchVisible:
        store.addressStore.feedbackCardRecentSearchVisible,
    }))
  );

  const latestSelectedAddress =
    latestSelectedAddresses.length > 0 ? latestSelectedAddresses[0] : null;

  const { data: serviceQualificationData } = useQuery(
    serviceQualificationQueryOptions({
      selectedAddress: latestSelectedAddress,
      isEnabled:
        feedbackCardRecentSearchVisible !== null &&
        feedbackCardRecentSearchVisible !== 'already-has-service',
    })
  );

  const {
    data: broadbandPlanCardData,
    isPending: isBroadbandPlanCardLoading,
    isError: isBroadbandPlanCardError,
  } = useQuery({
    queryFn: () => getBroadbandPlanCard(),
    queryKey: [...apiQueryKeys.getPlanCards, 'BROADBAND'],
    staleTime: Infinity,
  });

  const { data: featuresData } = useQuery({
    queryFn: () => getNewDevelopmentCharge(),
    queryKey: [...apiQueryKeys.newDevelopmentCharge],
    enabled:
      serviceQualificationData?.sqSummary.nbn?.newDevelopmentsChargeApplies ===
      true,
    staleTime: Infinity,
  });

  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: getQuoteSummary,
    mutationKey: [...apiMutationKeys.getQuoteSummary],
  });

  //NOTE: if page hasn't been loaded yet it will have problem scrolling it using hash
  React.useEffect(() => {
    if (broadbandPlanCardData && isBroadbandPlanCardLoading) {
      return;
    }

    const hash = window.location.hash;
    if (!hash) {
      return;
    }

    const replacedHash = hash.replace('#', '');
    if (replacedHash !== SCROLLABLE_IDS.BROADBAND_PLAN) {
      return;
    }

    const broadbandPlan = document.getElementById(
      SCROLLABLE_IDS.BROADBAND_PLAN
    );
    if (!broadbandPlan) {
      return;
    }

    broadbandPlan.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [broadbandPlanCardData, isBroadbandPlanCardLoading]);

  if (isBroadbandPlanCardLoading) {
    return (
      <div data-component="CMSPlanCardBroadband" className="h-full">
        <FullScreenLoadingSpinner />
      </div>
    );
  }

  if (isBroadbandPlanCardError) {
    return null;
  }

  // NOTE: we currently support just 1 broadband plan
  const planCardData = broadbandPlanCardData?.[0];

  // NOTE: API returns an array but we only need to pick the first one.
  const planCardPrice = planCardData.prices[0].amount;

  /* FIXME: Remove unnecessary optional chaining. Content and heading are required but for now optional. */
  const planCardHeading = planCardData.content?.heading ?? '';

  const buttonText =
    feedbackCardRecentSearchVisible === 'great-news'
      ? latestBroadbandOffer
        ? 'Added'
        : 'Add to Cart'
      : 'Check Address';

  const isButtonDisabled = buttonText === 'Added';

  const handleOnClickCheckAddress = () => {
    gtmCTaClick({ clickText: 'Check Address' });
    const element = document.getElementById(SCROLLABLE_IDS.ADDRESS_SEARCH);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleOnClick = () => {
    if (buttonText === 'Check Address') {
      handleOnClickCheckAddress();
      return;
    }

    if (!serviceQualificationData || latestBroadbandOffer != null) {
      //NOTE: If for some reason we don't have data from service qualification(from cache) we shouldn't continue the process
      //NOTE: if latest broadband offer already added in the cart we should disable handleOnClick
      return;
    }

    const shouldCheckForNewDevelopmentCharge =
      serviceQualificationData.sqSummary.nbn?.newDevelopmentsChargeApplies ===
      true;

    if (shouldCheckForNewDevelopmentCharge && !featuresData) {
      //NOTE: If for some reason we don't have data from features API we shouldn't continue the process
      //TODO: Log to Sentry
      return;
    }

    const mobileOfferPayload = latestMobileOffer
      ? createMobileOfferPayload(latestMobileOffer.id)
      : null;

    const broadbandOfferPayload = createBroadbandOfferPayload({
      broadbandPlanId: planCardData.id,
      NDCfeatureId: shouldCheckForNewDevelopmentCharge
        ? (featuresData?.id ?? null)
        : null,
    });

    mutate(
      {
        quoteSummaryRequest: {
          ...broadbandOfferPayload,
          //NOTE: Attach the mobile offer payload if it exists
          ...(mobileOfferPayload && {
            ...mobileOfferPayload,
          }),
        },
      },
      {
        onSuccess: () => {
          //NOTE: We're 100% sure that broadbandPlanCardData is not null, otherwise the component is not rendered, but TS doesn't know that
          if (broadbandPlanCardData) {
            setLatestBroadbandOffer(broadbandPlanCardData[0]);
          }
          //NOTE: We call Features API conditionally, but we can send null if it's not enabled
          setLatestNewDevelopmentCharge(
            shouldCheckForNewDevelopmentCharge ? (featuresData ?? null) : null
          );
          setIsShoppingCartOpen(true);
          const technologyShortCode =
            serviceQualificationData?.sqSummary?.nbn?.technologyShortCode ??
            serviceQualificationData?.sqSummary.nbn?.technology ??
            '';
          gtmSelectItem({
            currency: CURRENCY,
            value: planCardPrice,
            items: [
              {
                currency: CURRENCY,
                item_name: planCardHeading,
                item_category: NBN,
                item_category2: technologyShortCode,
                item_category3: NONE,
                price: planCardPrice,
                quantity: 1,
              },
            ],
          });
          gtmNBNPlanCardSelect({
            planName: planCardHeading,
            totalPrice: planCardPrice,
          });
        },
      }
    );

    return;
  };

  return (
    <div
      id={SCROLLABLE_IDS.BROADBAND_PLAN}
      data-component="CMSPlanCardBroadband"
      className="bg-white w-full h-full flex flex-col gap-[12.61px] p-[16px] pb-0 rounded-[16px] overflow-y-hidden md:p-6 md:pb-0 md:gap-[20px] lg:p-8 lg:pb-0 lg:pr-[29px] lg:gap-[24px]"
    >
      <div className="flex gap-[12.61px] md:gap-[16px] lg:gap-[24px]">
        <div>
          <WifiBoxIcon className="size-[48px] md:size-[52px] lg:size-[84px]" />
        </div>
        <div className="flex flex-col text-black gap-[2.1px] md:gap-0 lg:gap-[4px]">
          <h3 className="font-nunito700 text-sm md:text-lg lg:text-2xl">
            {planCardHeading}
          </h3>
          <p className="text-sm font-light md:text-lg lg:text-2xl">
            Typical evening speed:
            <br />
            {/* FIXME: Remove unnecessary optional chaining. Content and typical evening speed are required but for now optional. */}
            {planCardData.content?.typical_evening_speed}
          </p>
        </div>
      </div>

      <div className="text-black">
        <span className="text-2xl font-nunito700 md:text-3xl lg:text-4xl">
          ${planCardPrice}
        </span>
        <span className="text-xs font-nunito500 md:text-sm lg:text-lg">
          /mth
        </span>
      </div>
      <Separator className="bg-light-grey-500" />
      <div className="flex flex-col gap-[8.41px] md:gap-[12px] lg:gap-[16px]">
        {/* FIXME: Remove unnecessary optional chaining. Content and features are required but for now optional. */}
        {planCardData.content?.plan_features?.map((value, index) => {
          return (
            <div className="flex" key={index}>
              <CheckCircle className="size-4 shrink-0 text-black mr-[4px] md:size-5 lg:size-6 lg:mr-[8px]" />
              <div className="text-xs font-nunito500 text-black md:text-sm lg:text-base [&_a]:underline">
                <ReplaceHtmlWithReact value={value.title} />
              </div>
            </div>
          );
        })}
      </div>
      <Separator className="bg-light-grey-500" />
      {pageKey && pageKey !== 'speed-test' && (
        <Button
          disabled={isButtonDisabled}
          onClick={handleOnClick}
          loading={isMutating}
          className="relative w-full text-sm font-nunito700 h-[40px] lg:h-[52px] md:text-base lg:text-lg"
        >
          {buttonText}
        </Button>
      )}
      <House className="h-[101px] mt-auto md:h-[113.69px] lg:h-[192.36px]" />
    </div>
  );
};

export { CMSPlanCardBroadband };
